import { useState, useEffect, useRef, useMemo } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { useNavigate } from "react-router-dom";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  ClientFilterFields,
  buildClientListFilterChips,
  CLIENT_LIST_SORT_OPTIONS,
  CLIENT_STATUS_OPTIONS,
} from "../components/filters";
import capitalizeFilterLabel from "../components/filters/utils/capitalizeFilterLabel";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import usePageMeta from "../hooks/usePageMeta";
import Layout from "./Layout";
import { BASE_URL } from "../config";
import API from "../api";
import "../Styles/Personnel.css";
import ActionDropdown from "../components/ActionDropdown";
import { showError, showSuccess, showConfirm } from "../utils/notifications";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import ClientDetailModal from "../components/ClientDetailModal";
import ContactDetailModal from "../components/ContactDetailModal";
import PositionDetailModal from "../components/PositionDetailModal";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { useAppPermissions } from "../context/PermissionContext";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";
import { useDeleteKeyEasterEgg } from "../hooks/useDeleteKeyEasterEgg";

const CLIENTS_GEOLOCATION_REINDEX_CURSOR_KEY =
  "clients_geolocation_reindex_after_id";

function readStoredClientsGeolocationCursor() {
  try {
    const s = sessionStorage.getItem(CLIENTS_GEOLOCATION_REINDEX_CURSOR_KEY);
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStoredClientsGeolocationCursor(afterClientId) {
  try {
    if (afterClientId > 0) {
      sessionStorage.setItem(
        CLIENTS_GEOLOCATION_REINDEX_CURSOR_KEY,
        String(afterClientId),
      );
    } else {
      sessionStorage.removeItem(CLIENTS_GEOLOCATION_REINDEX_CURSOR_KEY);
    }
  } catch (_) {
    // ignore
  }
}

const getClientInitials = (name = "") => {
  const safe = String(name || "").trim();
  if (!safe) return "CL";
  const parts = safe.split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return safe.slice(0, 2).toUpperCase();
};

const ClientAvatar = ({ name, profilePicture }) => {
  const [imageError, setImageError] = useState(false);
  const src = resolveBackendUrl(profilePicture);
  const showImage = src && !imageError;

  return (
    <div
      className="w-6 h-6 rounded-full bg-gray-500 text-white flex items-center justify-center text-xs font-semibold overflow-hidden"
      title={name || "Client"}
      style={{ minWidth: "1.5rem", minHeight: "1.5rem" }}
    >
      {showImage ? (
        <img
          src={src}
          alt={name || "client avatar"}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{getClientInitials(name)}</span>
      )}
    </div>
  );
};

const CLIENT_TABS = ["CLIENTS", "CONTACTS", "POSITIONS"];

function formatClientStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  const match = CLIENT_STATUS_OPTIONS.find((o) => o.value === normalized);
  if (match) return match.label;
  if (normalized === "deleted") return "Deleted";
  if (normalized === "pending_activation" || normalized === "pending activation") {
    return "Pending Activation";
  }
  return capitalizeFilterLabel(status) || "—";
}

function clientStatusColorClass(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized === "active" ? "text-status-active" : "text-status-pending";
}

function defaultTabQueryState(tab) {
  return {
    rawSearch: "",
    searchTerm: "",
    currentPage: 1,
    filterSortBy:
      tab === "CONTACTS"
        ? "first_name"
        : tab === "POSITIONS"
          ? "name"
          : "all",
    filterSortDirection: "asc",
    showDeleted: false,
    divisionFilter: [],
    statusFilter: [],
    contactRoleFilter: [],
    contactClientFilter: [],
    positionClientFilter: [],
  };
}

const Clients = () => {
  usePageMeta("Clients");
  const navigate = useNavigate();
  const { canWriteClients } = useAppPermissions();
  const clientsReadOnly = !canWriteClients;
  const [data, setData] = useState([]);
  const [selectedTab, setSelectedTab] = useState("CLIENTS"); // CLIENTS, CONTACTS, POSITIONS
  const [tabQueryState, setTabQueryState] = usePersistentPageState(
    "page_filters_clients",
    () => Object.fromEntries(CLIENT_TABS.map((t) => [t, defaultTabQueryState(t)])),
  );
  const q = tabQueryState[selectedTab] ?? defaultTabQueryState(selectedTab);
  const patchQuery = (partial) => {
    setTabQueryState((prev) => ({
      ...prev,
      [selectedTab]: { ...prev[selectedTab], ...partial },
    }));
  };
  const {
    rawSearch,
    searchTerm,
    currentPage,
    filterSortBy,
    filterSortDirection,
    showDeleted,
    divisionFilter,
    statusFilter,
    contactRoleFilter,
    contactClientFilter,
    positionClientFilter,
  } = q;
  const [reloadKey, setReloadKey] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddPositionModal, setShowAddPositionModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null); // clicked client
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showPositionDetailModal, setShowPositionDetailModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [contactDetailInitialTab, setContactDetailInitialTab] = useState("profile");
  const [showAssignContactModal, setShowAssignContactModal] = useState(false);
  const [assignContact, setAssignContact] = useState(null);
  const [geolocationBulkLoading, setGeolocationBulkLoading] = useState(false);
  const [geolocationReindexAfterId, setGeolocationReindexAfterId] = useState(
    readStoredClientsGeolocationCursor,
  );
  const { showEasterEgg: showAdminEasterEgg } = useDeleteKeyEasterEgg();
  const [assignClients, setAssignClients] = useState([]);
  const [assignRoles, setAssignRoles] = useState([]);
  const [assignLocations, setAssignLocations] = useState([]);
  const [assignClientId, setAssignClientId] = useState("");
  const [assignLocationId, setAssignLocationId] = useState("");
  const [assignRoleIds, setAssignRoleIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState("Client");
  const [showSubClientModal, setShowSubClientModal] = useState(false);
  const [parentClient, setParentClient] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [employmentOfficeOptions, setEmploymentOfficeOptions] = useState([]);
  const [newClientPhone, setNewClientPhone] = useState("");
  const [subClientPhone, setSubClientPhone] = useState("");
  const [newClientPrimaryLocation, setNewClientPrimaryLocation] = useState("");
  const [newClientLocationName, setNewClientLocationName] = useState("");
  const [newClientBillingAddress, setNewClientBillingAddress] = useState("");
  const [addClientSaving, setAddClientSaving] = useState(false);
  const [subClientPrimaryLocation, setSubClientPrimaryLocation] = useState("");
  const [subClientBillingAddress, setSubClientBillingAddress] = useState("");
  const [subClientSaving, setSubClientSaving] = useState(false);
  const filterPanel = useFilterPanel();
  const [locationSearch, setLocationSearch] = useState("");
  const [contactClientOptions, setContactClientOptions] = useState([]);
  const addClientFormRef = useRef(null);
  const addClientSavingRef = useRef(false);

  const validatePhone = (phone) => Boolean(phone) && isValidPhoneNumber(phone);

  const resetAddClientForm = () => {
    setNewClientPhone("");
    setNewClientPrimaryLocation("");
    setNewClientLocationName("");
    setNewClientBillingAddress("");
    if (addClientFormRef.current) addClientFormRef.current.reset();
  };

  const buildQuery = () => {
    const params = new URLSearchParams();

    params.set("page", currentPage);
    params.set("limit", rowsPerPage);
    params.set("search", searchTerm);

    // ✅ backend sorting
    params.set("sortBy", filterSortBy === "all" ? "name" : filterSortBy);
    params.set("sortDirection", filterSortDirection);

    // ✅ show deleted checkbox
    params.set("showDeleted", showDeleted ? "1" : "0");

    if (selectedTab === "CONTACTS") {
      if (statusFilter.length > 0) params.set("status", statusFilter.join(","));
      if (contactRoleFilter.length > 0) params.set("role", contactRoleFilter.join(","));
      if (contactClientFilter.length > 0) params.set("clientId", contactClientFilter.join(","));
    } else if (selectedTab === "POSITIONS") {
      if (divisionFilter.length > 0) params.set("divisionIds", divisionFilter.join(","));
      if (positionClientFilter.length > 0) params.set("clientIds", positionClientFilter.join(","));
    } else {
      // ✅ multi values (send as comma separated)
      if (statusFilter.length > 0) params.set("status", statusFilter.join(","));
      if (divisionFilter.length > 0)
        params.set("divisionIds", divisionFilter.join(","));
    }

    return params.toString();
  };

  // Fetch data whenever page, search, or tab changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        let endpoint;
        if (selectedTab === "CLIENTS") endpoint = "clients";
        else if (selectedTab === "CONTACTS") endpoint = "contacts";
        else endpoint = "positions";

        console.log(`📋 [CLIENTS-TAB] Fetching ${endpoint} data - page: ${currentPage}, search: "${searchTerm}"`);

        const res = await fetch(
          `${BASE_URL}/api/clients/${endpoint}?${buildQuery()}`,
          {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        const result = await res.json();
        console.log(`📋 [CLIENTS-TAB] Got ${(result.data || []).length} ${endpoint}. First: "${result.data?.[0]?.name}"`);
        console.log(`📋 [CLIENTS-TAB] Total count from API:`, result.total, `Total pages:`, result.totalPages);
        setData(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.total || 0);
      } catch (err) {
        console.error(err);
        setData([]);
      }
    };
    fetchData();
  }, [selectedTab, reloadKey, rowsPerPage, tabQueryState]);

  // Clear table while switching tabs to avoid showing the previous tab's rows
  useEffect(() => {
    setData([]);
  }, [selectedTab]);

  useEffect(() => {
    patchQuery({ currentPage: 1 });
  }, [rowsPerPage]);

  const getDefaultLocationId = (locationRows = []) => {
    const primaryLoc = locationRows.find((loc) => {
      const p = loc?.primary;
      return p === 1 || p === true || String(p) === "1";
    });
    const target = primaryLoc || locationRows[0];
    return target ? String(target.location_id || target.client_location_id || "") : "";
  };

  const openAssignContactModal = async (contact) => {
    setAssignContact(contact);
    setShowAssignContactModal(true);
    setAssignClientId("");
    setAssignLocationId("");
    setAssignRoleIds([]);
    setAssignLocations([]);
    try {
      const [clientsRes, rolesRes] = await Promise.all([
        fetch(`${BASE_URL}/api/clients/clients?limit=1000`),
        API.get("/api/personnel/roles", { params: { level: "client" } }),
      ]);
      const clientsJson = await clientsRes.json().catch(() => ({}));
      const rolesData = Array.isArray(rolesRes?.data?.data) ? rolesRes.data.data : [];
      setAssignClients(Array.isArray(clientsJson?.data) ? clientsJson.data : []);
      setAssignRoles(rolesData);
    } catch (err) {
      console.error("Failed to load assign modal data:", err);
      showError("Failed to load clients/roles");
    }
  };

  const closeAssignContactModal = () => {
    setShowAssignContactModal(false);
    setAssignContact(null);
    setAssignClients([]);
    setAssignRoles([]);
    setAssignLocations([]);
    setAssignClientId("");
    setAssignLocationId("");
    setAssignRoleIds([]);
    setAssignSaving(false);
  };

  useEffect(() => {
    if (!showAssignContactModal || !assignClientId) return;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/clients/${assignClientId}/locations`);
        const json = await res.json().catch(() => ({}));
        const nextLocations = Array.isArray(json?.data) ? json.data : [];
        setAssignLocations(nextLocations);
        setAssignLocationId(getDefaultLocationId(nextLocations));
      } catch (err) {
        console.error("Failed to load locations:", err);
        setAssignLocations([]);
        setAssignLocationId("");
      }
    })();
  }, [showAssignContactModal, assignClientId]);

  const toggleAssignRole = (roleId) => {
    const id = Number(roleId);
    setAssignRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAssignContactToClient = async () => {
    if (!assignContact?.id) return showError("Contact is missing");
    if (!assignClientId) return showError("Client is required");
    if (!assignRoleIds.length) return showError("At least one role is required");
    setAssignSaving(true);
    try {
      const targetId = assignLocationId || assignClientId;
      const payload = {
        objectId: Number(targetId),
        roleIds: assignRoleIds,
      };
      const res = await fetch(`${BASE_URL}/api/personnel/accounts/${assignContact.id}/client-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to assign contact");
      }
      showSuccess("Contact assigned to client");
      closeAssignContactModal();
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.error("Assign contact to client failed:", err);
      showError(err.message || "Server error");
    } finally {
      setAssignSaving(false);
    }
  };
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/clients/divisions`);
        const result = await res.json();
        setDivisions(result.data || []);
      } catch (err) {
        console.error("Error fetching divisions:", err);
        setDivisions([]);
      }
    };
    fetchDivisions();
  }, []);

  useEffect(() => {
    fetch(`${BASE_URL}/api/personnel/offices`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) =>
        setEmploymentOfficeOptions(Array.isArray(j) ? j : j.data || []),
      )
      .catch(() => setEmploymentOfficeOptions([]));
  }, []);

  useEffect(() => {
    const fetchContactClients = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/clients?all=1`);
        const result = await res.json();
        setContactClientOptions(
          Array.isArray(result?.data)
            ? result.data.map((row) => ({
                value: String(row.id),
                label: typeof row.name === "string" ? row.name : String(row.name || ""),
              }))
            : [],
        );
      } catch (err) {
        console.error("Error fetching clients for contact filter:", err);
        setContactClientOptions([]);
      }
    };
    fetchContactClients();
  }, []);

  const geolocationReindexHasCursor = geolocationReindexAfterId > 0;

  const handleBulkReindexClientGeolocation = async () => {
    const continuing = geolocationReindexAfterId > 0;
    const confirmMsg = continuing
      ? `Continue fixing client billing geolocation after client ID ${geolocationReindexAfterId}? Up to 10,000 per run.`
      : "Fix geolocation for all client billing addresses with missing coordinates (up to 10,000 per run)?";
    if (!window.confirm(confirmMsg)) return;
    setGeolocationBulkLoading(true);
    try {
      const res = await API.post("/api/clients/reindex-geolocation", {
        after_client_id: geolocationReindexAfterId,
      });
      const j = res.data || {};
      let msg = `Geolocation batch: ${j.succeeded} succeeded, ${j.failed} failed (${j.processed} processed).`;
      if (j.truncated && j.nextAfterClientId != null) {
        setGeolocationReindexAfterId(j.nextAfterClientId);
        writeStoredClientsGeolocationCursor(j.nextAfterClientId);
        msg += ` About ${j.remainingEstimate} remaining — click again to continue.`;
      } else {
        setGeolocationReindexAfterId(0);
        writeStoredClientsGeolocationCursor(0);
      }
      showSuccess(msg, continuing ? "Fix geolocation (continue)" : "Fix geolocation");
      if (j.errorsTruncated) {
        showError(
          "Additional per-client errors were omitted from the response.",
          "Fix geolocation",
        );
      }
    } catch (err) {
      console.error(err);
      showError(
        err.response?.data?.message || err.message || "Fix geolocation failed",
        "Fix geolocation",
      );
    } finally {
      setGeolocationBulkLoading(false);
    }
  };

  // Helper to compute a compact page list with ellipses
  const getPageList = (total, current) => {
    const delta = 2; // neighbors to show
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  const cleanSearchText = (text) => {
    return text
      .toLowerCase()
      .replace(/[-/]/g, " ")
      .replace(/[^a-z0-9\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  // get division
  const getDivision = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/clients/divisions`);
      const result = await res.json();
      setDivisions(result.data || []);
    } catch (err) {
      console.error("Error fetching divisions:", err);
      setDivisions([]);
    }
  };

  useEffect(() => {
    getDivision();
  }, []);

  const divisionOptions = divisions.map((d) => ({
    value: d.division_id || d.id, // ✅ unique id
    label: typeof d.name === 'string' ? d.name : String(d.name || ''), // ✅ ensure string
  }));

  const clearAllClientFilters = () => {
    setTabQueryState((prev) => ({
      ...prev,
      [selectedTab]: defaultTabQueryState(selectedTab),
    }));
    setReloadKey((prev) => prev + 1);
  };

  const removeClientFilter = (key) => {
    if (key === "showDeleted") {
      patchQuery({ showDeleted: false });
      return;
    }
    if (key === "filterSortBy") {
      patchQuery({
        filterSortBy:
          selectedTab === "CONTACTS"
            ? "first_name"
            : selectedTab === "POSITIONS"
              ? "name"
              : "all",
        filterSortDirection: "asc",
      });
      return;
    }
    if (key === "divisionFilter" || key === "statusFilter" || key === "contactRoleFilter" || key === "contactClientFilter" || key === "positionClientFilter") {
      patchQuery({ [key]: [] });
    }
  };

  const clientListFilterChips = useMemo(
    () =>
      buildClientListFilterChips(selectedTab, q, {
        divisionOptions: divisions,
        contactClientOptions,
      }),
    [selectedTab, q, divisions, contactClientOptions],
  );

  const toolbarSortOptions = useMemo(() => {
    const opts = CLIENT_LIST_SORT_OPTIONS[selectedTab] || [];
    return selectedTab === "CLIENTS" ? opts.filter((o) => o.value !== "all") : opts;
  }, [selectedTab]);

  const toolbarSortBy =
    selectedTab === "CLIENTS" && filterSortBy === "all" ? "name" : filterSortBy;

  const resultsStart = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const resultsEnd =
    totalCount === 0
      ? 0
      : Math.min((currentPage - 1) * rowsPerPage + data.length, totalCount);

  const normalizeTableText = (value, fallback = "") => {
    if (value == null) return fallback;

    const raw = typeof value === "string" ? value : String(value);
    const cleaned = raw
      .replace(/â€”/g, "")
      .replace(/â€“/g, "")
      .replace(/â€˜/g, "")
      .replace(/â€™/g, "")
      .replace(/â€œ/g, "")
      .replace(/â€/g, "")
      .trim();

    return cleaned || fallback;
  };

  const isSortableColumnForTab = (columnKey) => {
    if (selectedTab === "CONTACTS") {
      return ["first_name", "last_name"].includes(columnKey);
    }
    if (selectedTab === "POSITIONS") {
      return columnKey === "name";
    }
    return ["name", "parent", "status"].includes(columnKey);
  };

  const handleHeaderSort = (columnKey) => {
    if (!isSortableColumnForTab(columnKey)) return;
    if (filterSortBy === columnKey) {
      patchQuery({
        filterSortDirection: filterSortDirection === "asc" ? "desc" : "asc",
      });
      return;
    }
    patchQuery({ filterSortBy: columnKey, filterSortDirection: "asc" });
  };

  const SortArrow = ({ colKey }) => {
    if (!isSortableColumnForTab(colKey)) return null;

    return (
      <button
        type="button"
        onClick={() => handleHeaderSort(colKey)}
        className="float-right mr-1 text-sm text-white/90 hover:text-white"
        title="Sort"
      >
        {filterSortBy === colKey
          ? filterSortDirection === "asc"
            ? "▲"
            : "▼"
          : "⇅"}
      </button>
    );
  };

  return (
    <Layout active="Clients">
      <style>{`
        .PhoneInput {
          display: flex;
          align-items: center;
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          padding: 8px 12px;
          background: white;
        }
        .PhoneInput:focus-within {
          border-color: #DC1F00;
          box-shadow: 0 0 0 1px #DC1F00;
        }
        .PhoneInputInput {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          background: transparent;
          margin-left: 8px;
        }
        .PhoneInputCountry {
          display: flex;
          align-items: center;
          margin-right: 8px;
        }
      `}</style>
      <div className="py-3 px-2 flex flex-col p-6 min-h-screen no-mobile-table-stack">
        <AtlasPageHeader title="Clients" />
        <AtlasPageDivider className="mx-2 sm:mx-6" />

        <div className="mt-4 sm:mt-6 mx-2 sm:mx-6 p-3 sm:p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col min-w-0">
          {/* Tabs */}
          <AtlasTabBar
            tabs={["CLIENTS", "CONTACTS", "POSITIONS"]}
            activeTab={selectedTab}
            onTabChange={setSelectedTab}
          />
          <div className={`min-w-0 max-w-full ${filterPanel.contentOffsetClass}`}>
          <AtlasListToolbar
            searchValue={rawSearch}
            onSearchChange={(value) =>
              patchQuery({
                rawSearch: value,
                searchTerm: cleanSearchText(value),
                currentPage: 1,
              })
            }
            searchPlaceholder={`Search ${selectedTab.toLowerCase()}`}
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={() => {
              patchQuery({ currentPage: 1 });
              setReloadKey((k) => k + 1);
            }}
            resultsMeta={{
              startRow: resultsStart,
              endRow: resultsEnd,
              totalCount,
              pageSize: rowsPerPage,
              pageSizeOptions: [10, 25, 50, 100],
              onPageSizeChange: (size) => {
                setRowsPerPage(size);
                patchQuery({ currentPage: 1 });
              },
              sortBy: toolbarSortBy,
              sortOrder: filterSortDirection,
              sortOptions: toolbarSortOptions,
              onSortByChange: (value) => patchQuery({ filterSortBy: value }),
              onSortOrderToggle: () =>
                patchQuery({
                  filterSortDirection:
                    filterSortDirection === "asc" ? "desc" : "asc",
                }),
            }}
            actions={
              <>
                {!clientsReadOnly && selectedTab === "CLIENTS" && showAdminEasterEgg && (
                  <button
                    type="button"
                    disabled={geolocationBulkLoading}
                    onClick={handleBulkReindexClientGeolocation}
                    className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {geolocationBulkLoading
                      ? "Fixing geolocation…"
                      : geolocationReindexHasCursor
                        ? "Fix geolocation (continue)"
                        : "Fix geolocation"}
                  </button>
                )}
                {!clientsReadOnly && selectedTab === "CLIENTS" && (
                  <button
                    type="button"
                    onClick={() => setShowAddClientModal(true)}
                    className="rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                  >
                    Add Client
                  </button>
                )}
                {!clientsReadOnly && selectedTab === "POSITIONS" && (
                  <button
                    type="button"
                    onClick={() => setShowAddPositionModal(true)}
                    className="rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                  >
                    Add Position
                  </button>
                )}
              </>
            }
            chipBar={
              <AtlasFilterChipBar
                chips={clientListFilterChips}
                onChipClick={(chip) => {
                  if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                  else filterPanel.open();
                }}
                onRemove={(chip) => removeClientFilter(chip.key)}
                onClearAll={clearAllClientFilters}
              />
            }
          />

          {/* Table and Add Position */}
          <div className="flex-1 min-h-0 min-w-0">
            <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto h-full table-force-rows">
              {/* CLIENTS table */}
              {selectedTab === "CLIENTS" && (
                <table className="w-full min-w-[1100px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                    <tr>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "14%" }}
                      >
                        <SortArrow colKey="name" />
                        Name
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "12%" }}
                      >
                        <SortArrow colKey="parent" />
                        Parent Client
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "10%" }}
                      >
                        Divisions
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "12%" }}
                      >
                        Phone
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "16%" }}
                      >
                        Email
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "14%" }}
                      >
                        Sub Clients
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "8%" }}
                      >
                        <SortArrow colKey="status" />
                        Status
                      </th>
                      {!clientsReadOnly ? (
                      <th className="py-2 px-1" style={{ width: "6%" }}></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((client) => (
                      <tr
                        key={client.id}
                        className="border-t border-border hover:bg-table-row-hover transition-colors align-top"
                      >
                        <td
                          className="py-2 px-1 text-red-600 cursor-pointer font-medium text-sm"
                          style={{ width: "14%" }}
                          onClick={async () => {
                            try {
                              // Prefetch client details so modal has billing address immediately
                              const res = await fetch(
                                `${BASE_URL}/api/clients/${client.id}/details`,
                              );
                              const j = await res.json().catch(() => ({}));
                              const detailed = j.data || client;
                              setSelectedClient(detailed);
                            } catch (e) {
                              console.warn(
                                "Failed to prefetch client details, falling back to basic client object",
                                e,
                              );
                              setSelectedClient(client);
                            } finally {
                              setShowClientDetailModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 break-words">
                            <ClientAvatar
                              name={typeof client.name === "string" ? client.name : String(client.name || "")}
                              profilePicture={
                                client.profile_picture || `/api/avatars/client/${client.id}`
                              }
                            />
                            <span>
                              {(typeof client.name === "string" ? client.name : String(client.name || "")) || ""}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "12%" }}>
                          <div className="break-words text-sm">
                            {(typeof client.parentName === 'string' ? client.parentName : String(client.parentName || '')) || ""}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "10%" }}>
                          <div className="break-words text-sm">
                            {(typeof client.divisions === 'string' ? client.divisions : String(client.divisions || '')) || ""}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "12%" }}>
                          <div className="break-words text-sm">
                            {(typeof client.phone === 'string' ? client.phone : String(client.phone || '')) || ""}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "16%" }}>
                          <div className="break-words text-sm">
                            {(typeof client.email === 'string' ? client.email : String(client.email || '')) || ""}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "14%" }}>
                          <div className="break-words text-sm">
                            {(() => {
                              const count =
                                client.subClientCount != null
                                  ? Number(client.subClientCount)
                                  : Array.isArray(client.subClients)
                                    ? client.subClients.length
                                    : 0;
                              return count > 0 ? String(count) : "";
                            })()}
                          </div>
                        </td>
                        <td className="py-2 px-1 break-words text-sm" style={{ width: "8%" }}>
                          <span
                            className={`text-sm font-medium ${clientStatusColorClass(client.status)}`}
                          >
                            {formatClientStatus(client.status)}
                          </span>
                        </td>
                        {!clientsReadOnly ? (
                        <td
                          className="py-1 px-1 text-right"
                          style={{ width: "6%" }}
                        >
                          <ActionDropdown
                            actions={
                              showDeleted
                                ? [
                                    {
                                      label: "Activate",
                                      onClick: async () => {
                                        try {
                                          if (
                                            !(await showConfirm(
                                              `Are you sure you want to activate ${typeof client.name === "string" ? client.name : "this client"}?`,
                                            ))
                                          )
                                            return;
                                          const res = await fetch(
                                            `${BASE_URL}/api/clients/clients/${client.id}/activate`,
                                            { method: "PUT" },
                                          );
                                          const data = await res.json().catch(() => ({}));
                                          if (!res.ok) {
                                            throw new Error(data.message || "Activate failed");
                                          }
                                          showSuccess("Client activated");
                                          patchQuery({ currentPage: 1 });
                                          setReloadKey((prev) => prev + 1);
                                        } catch (err) {
                                          console.error(err);
                                          showError(err.message || "Server error");
                                        }
                                      },
                                    },
                                  ]
                                : [
                                    {
                                      label: "Create Sub-client",
                                      onClick: () => {
                                        setParentClient(client);
                                        setShowSubClientModal(true);
                                      },
                                    },
                                    {
                                      label: "Delete",
                                      onClick: async () => {
                                        try {
                                          if (!(await showConfirm(`Are you sure you want to delete ${typeof client.name === "string" ? client.name : "this client"}?`))) return;
                                          const res = await fetch(
                                            `${BASE_URL}/api/clients/clients/${client.id}`,
                                            { method: "DELETE" },
                                          );
                                          const data = await res.json();
                                          if (!res.ok)
                                            throw new Error(
                                              data.message || "Delete failed",
                                            );
                                          showSuccess("Client deleted");
                                          patchQuery({ currentPage: 1 });
                                          setReloadKey((prev) => prev + 1);
                                        } catch (err) {
                                          console.error(err);
                                          showError(err.message || "Server error");
                                        }
                                      },
                                      danger: true,
                                    },
                                  ]
                            }
                          />
                        </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* CONTACTS table */}
              {selectedTab === "CONTACTS" && (
                <table className="w-full min-w-[700px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                    <tr>
                      <th
                        className="py-2 px-2 text-left text-xs font-medium text-white"
                        style={{ width: "45%" }}
                      >
                        <SortArrow colKey="first_name" />
                        Name
                      </th>
                      <th
                        className="py-2 px-2 text-left text-xs font-medium text-white"
                        style={{ width: "45%" }}
                      >
                        Client
                      </th>
                      {!clientsReadOnly ? (
                      <th className="py-2 px-2" style={{ width: "10%" }}></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((contact) => (
                      <tr
                        key={contact.id}
                        className="border-t border-border hover:bg-table-row-hover transition-colors"
                      >
                        <td className="py-2 px-2" style={{ width: "45%" }}>
                          <div>
                            <span
                              className="text-red-600 cursor-pointer font-medium text-sm"
                              onClick={() => {
                                setSelectedContact(contact);
                                setContactDetailInitialTab("profile");
                                setShowContactDetail(true);
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 border border-red-100 flex items-center justify-center overflow-hidden shrink-0">
                                  {contact.profile_picture ? (
                                    <img
                                      src={resolveBackendUrl(contact.profile_picture)}
                                      alt={typeof contact.name === "string" ? contact.name : "Contact"}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        const fallback = e.currentTarget.nextElementSibling;
                                        if (fallback) fallback.style.display = "flex";
                                      }}
                                    />
                                  ) : null}
                                  <span
                                    className="text-xs font-bold"
                                    style={{
                                      display: contact.profile_picture ? "none" : "flex",
                                    }}
                                  >
                                    {String(contact?.name || "")
                                      .trim()
                                      .charAt(0)
                                      .toUpperCase() || "C"}
                                  </span>
                                </div>
                                <div className="break-words">
                                  {(typeof contact.name === "string"
                                    ? contact.name
                                    : String(contact.name || "")) || ""}
                                </div>
                              </div>
                            </span>
                          </div>
                        </td>
                        <td className="py-2 px-2" style={{ width: "45%" }}>
                          <div className="space-y-1">
                            {(Array.isArray(contact.clientRoles) && contact.clientRoles.length > 0
                              ? contact.clientRoles
                              : contact.clientName
                                ? [
                                    {
                                      clientId: contact.clientId,
                                      clientName: contact.clientName,
                                      locationName: "",
                                      roles: contact.role
                                        ? String(contact.role).split(", ")
                                        : [],
                                      displayLabel: contact.clientName,
                                    },
                                  ]
                                : []
                            ).map((clientRole) => (
                              <div
                                key={`${contact.id}-${clientRole.clientId}-${clientRole.locationName || clientRole.clientName}`}
                                className="break-words text-sm"
                              >
                                {clientRole.displayLabel ||
                                  `${clientRole.locationName || clientRole.clientName}${
                                    clientRole.roles?.length
                                      ? `: ${clientRole.roles.join(", ")}`
                                      : ""
                                  }`}
                              </div>
                            ))}
                          </div>
                        </td>
                        {!clientsReadOnly ? (
                        <td
                          className="py-1 px-2 text-right"
                          style={{ width: "10%" }}
                        >
                          <ActionDropdown
                            actions={
                              showDeleted
                                ? [
                                    {
                                      label: "Restore",
                                      onClick: async () => {
                                        try {
                                          if (
                                            !(await showConfirm(
                                              `Are you sure you want to restore ${typeof contact.name === "string" ? contact.name : "this contact"}?`,
                                            ))
                                          )
                                            return;
                                          const restoreClientId =
                                            contact.clientRoles?.[0]?.clientId ||
                                            contact.clientId;
                                          if (!restoreClientId) {
                                            showError("No client mapping found to restore");
                                            return;
                                          }
                                          const res = await fetch(
                                            `${BASE_URL}/api/clients/${restoreClientId}/client-contacts/${contact.id}/restore`,
                                            { method: "PUT" },
                                          );
                                          const data = await res.json().catch(() => ({}));
                                          if (!res.ok) {
                                            throw new Error(data.message || "Restore failed");
                                          }
                                          showSuccess("Contact restored");
                                          patchQuery({ currentPage: 1 });
                                          setReloadKey((prev) => prev + 1);
                                        } catch (err) {
                                          console.error(err);
                                          showError(err.message || "Server error");
                                        }
                                      },
                                    },
                                  ]
                                : [
                                    {
                                      label: "Assign Contact to Client",
                                      onClick: () => openAssignContactModal(contact),
                                    },
                                  ]
                            }
                          />
                        </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* POSITIONS table */}
              {selectedTab === "POSITIONS" && (
                <table className="w-full min-w-[1000px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                    <tr>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "16%" }}
                      >
                        <SortArrow colKey="name" />
                        Position
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "15%" }}
                      >
                        Client
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "10%" }}
                      >
                        Bill Rate
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "10%" }}
                      >
                        Pay Rate
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "25%" }}
                      >
                        Requirements
                      </th>
                      <th
                        className="py-2 px-1 text-left text-xs font-medium text-white"
                        style={{ width: "16%" }}
                      >
                        Uniform
                      </th>
                      {!clientsReadOnly ? (
                      <th className="py-2 px-1" style={{ width: "8%" }}></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((pos) => {
                      const isArchived =
                        String(pos?.state || "").toLowerCase() === "archived";

                      return (
                        <tr
                          key={pos.id}
                          className="border-t border-border hover:bg-table-row-hover transition-colors"
                        >
                        <td className="py-2 px-1" style={{ width: "16%" }}>
                          <div className="break-words">
                            <span
                              className={`cursor-pointer font-medium text-sm ${
                                isArchived
                                  ? "text-yellow-700 hover:text-yellow-800"
                                  : "text-red-600 hover:text-red-700"
                              }`}
                              onClick={async () => {
                                try {
                                  // Fetch full details so modal has division,
                                  // unpaid_break, skills, certifications, etc.
                                  const res = await fetch(
                                    `${BASE_URL}/api/clients/positions/${pos.id}`
                                  );
                                  const j = await res.json().catch(() => ({}));
                                  const detailed = j.data || pos;
                                  console.log('📋 [POSITION DETAIL] Received:', detailed);
                                  console.log('📋 [POSITION DETAIL] divisionName:', detailed.divisionName);
                                  setSelectedPosition(detailed);
                                } catch (e) {
                                  console.warn(
                                    "Failed to fetch position detail; falling back to row object",
                                    e
                                  );
                                  setSelectedPosition(pos);
                                } finally {
                                  setShowPositionDetailModal(true);
                                }
                              }}
                            >
                              {normalizeTableText(pos.name)}
                            </span>
                            {isArchived && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800">
                                Archived
                              </span>
                            )}
                            {pos.divisions && (
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                {normalizeTableText(pos.divisions)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "15%" }}>
                          <div className="break-words text-sm">
                            {normalizeTableText(pos.clientName)}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "10%" }}>
                          <div className="break-words text-sm">
                            {normalizeTableText(pos.bill_rate)}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "10%" }}>
                          <div className="break-words text-sm">
                            {normalizeTableText(pos.pay_rate)}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "25%" }}>
                          <div className="break-words text-sm">
                            {normalizeTableText(pos.requirements)}
                          </div>
                        </td>
                        <td className="py-2 px-1" style={{ width: "16%" }}>
                          <div className="break-words text-sm">
                            {normalizeTableText(pos.uniform)}
                          </div>
                        </td>
                        {!clientsReadOnly ? (
                        <td
                          className="py-1 px-1 text-right"
                          style={{ width: "8%" }}
                        >
                          <ActionDropdown
                            actions={[
                              ...(showDeleted
                                ? [
                                    {
                                      label: "Restore",
                                      onClick: async () => {
                                        try {
                                          if (
                                            !(await showConfirm(
                                              `Are you sure you want to restore ${typeof pos.name === "string" ? pos.name : "this position"}?`,
                                            ))
                                          )
                                            return;
                                          const clientIdForRestore =
                                            pos.clientId != null ? pos.clientId : 0;
                                          const res = await fetch(
                                            `${BASE_URL}/api/clients/${clientIdForRestore}/positions/${pos.id}/restore`,
                                            { method: "PUT" },
                                          );
                                          const data = await res.json().catch(() => ({}));
                                          if (!res.ok)
                                            throw new Error(
                                              data.message || "Restore failed",
                                            );
                                          showSuccess("Position restored");
                                          patchQuery({ currentPage: 1 });
                                          setReloadKey((prev) => prev + 1);
                                        } catch (err) {
                                          console.error(err);
                                          showError(err.message || "Server error");
                                        }
                                      },
                                    },
                                  ]
                                : [
                                    {
                                      label: isArchived ? "Unarchive" : "Archive",
                                      onClick: async () => {
                                        try {
                                          const clientIdForUpdate =
                                            pos.clientId != null ? pos.clientId : 0;
                                          const positionName =
                                            typeof pos.name === "string"
                                              ? pos.name
                                              : "this position";
                                          const confirmText = isArchived
                                            ? `Are you sure you want to unarchive ${positionName}?`
                                            : `Are you sure you want to archive ${positionName}?`;
                                          if (!(await showConfirm(confirmText))) return;

                                          const res = await fetch(
                                            `${BASE_URL}/api/clients/${clientIdForUpdate}/positions/${pos.id}/${isArchived ? "unarchive" : "archive"}`,
                                            { method: "PUT" },
                                          );
                                          const data = await res.json().catch(() => ({}));
                                          if (!res.ok)
                                            throw new Error(
                                              data.message || "Failed to update position",
                                            );

                                          showSuccess(
                                            isArchived
                                              ? "Position unarchived"
                                              : "Position archived",
                                          );
                                          patchQuery({ currentPage: 1 });
                                          setReloadKey((prev) => prev + 1);
                                        } catch (err) {
                                          console.error(err);
                                          showError(err.message || "Server error");
                                        }
                                      },
                                    },
                                    ...(!isArchived
                                      ? [
                                          {
                                            label: "Delete",
                                            onClick: async () => {
                                              try {
                                                if (
                                                  !(await showConfirm(
                                                    `Are you sure you want to delete ${typeof pos.name === "string" ? pos.name : "this position"}?`,
                                                  ))
                                                )
                                                  return;
                                                const clientIdForDelete =
                                                  pos.clientId != null ? pos.clientId : 0;
                                                const res = await fetch(
                                                  `${BASE_URL}/api/clients/${clientIdForDelete}/positions/${pos.id}`,
                                                  { method: "DELETE" },
                                                );
                                                const data = await res.json();
                                                if (!res.ok)
                                                  throw new Error(
                                                    data.message || "Delete failed",
                                                  );
                                                showSuccess("Position deleted");
                                                patchQuery({ currentPage: 1 });
                                                setReloadKey((prev) => prev + 1);
                                              } catch (err) {
                                                console.error(err);
                                                showError(err.message || "Server error");
                                              }
                                            },
                                            danger: true,
                                          },
                                        ]
                                      : []),
                                  ]),
                            ]}
                          />
                        </td>
                        ) : null}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Add Position Modal */}
        {showAddPositionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
              <button
                onClick={() => setShowAddPositionModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                ✖
              </button>
              <h2 className="text-xl font-semibold mb-4">Add Position</h2>
              <AddPositionForm
                BASE_URL={BASE_URL}
                onCancel={() => setShowAddPositionModal(false)}
                onSuccess={(createdPosition) => {
                  // Close the simple mapping modal and refresh the POSITIONS table
                  setShowAddPositionModal(false);
                  patchQuery({ currentPage: 1 });

                  // If we have details for the created/mapped position,
                  // immediately open the detailed Position modal so the user
                  // can enter bill rate, pay rate, unpaid break, uniform, etc.
                  if (createdPosition) {
                    setSelectedPosition(createdPosition);
                    setShowPositionDetailModal(true);
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Pagination: numbered, compact with ellipses, right-aligned */}
        <div className="flex items-center justify-end mt-4">
          <nav
            className="flex items-center justify-between mt-auto text-sm text-muted-foreground space-x-2 flex-wrap gap-y-2"
            aria-label="Pagination"
          >
            <button
              onClick={() =>
                patchQuery({ currentPage: Math.max(currentPage - 1, 1) })
              }
              disabled={currentPage === 1}
              className="px-2.5 py-1.5 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>

            {getPageList(totalPages || 1, currentPage).map((p, idx) => {
              if (typeof p === "string") {
                return (
                  <span key={`dots-${idx}`} className="px-2.5 py-1.5">
                    …
                  </span>
                );
              }
              return (
                <button
                  key={p}
                  onClick={() => patchQuery({ currentPage: p })}
                  aria-current={p === currentPage ? "page" : undefined}
                  className={`px-2.5 py-1.5 border border-[#DC1F0033] rounded-md ${p === currentPage
                    ? "bg-[#DC1F00] text-white"
                    : "hover:bg-gray-100"
                    }`}
                >
                  {p}
                </button>
              );
            })}

            <button
              onClick={() =>
                patchQuery({
                  currentPage: Math.min(currentPage + 1, totalPages || 1),
                })
              }
              disabled={currentPage === totalPages}
              className="px-2.5 py-1.5 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
          </div>

          <AtlasFilterPanel
            isOpen={filterPanel.isOpen}
            onClose={filterPanel.close}
            onClear={clearAllClientFilters}
            activeCount={clientListFilterChips.length}
            title="Client Filters"
          >
            <ClientFilterFields
              selectedTab={selectedTab}
              query={q}
              patchQuery={patchQuery}
              focusedFieldId={filterPanel.focusedFieldId}
              onFocusClear={filterPanel.clearFocus}
              divisionOptions={divisionOptions}
              contactClientOptions={contactClientOptions}
            />
          </AtlasFilterPanel>
      </div>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
            <button
              onClick={() => {
                if (addClientSaving) return;
                setShowAddClientModal(false);
                resetAddClientForm();
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              disabled={addClientSaving}
            >
              ✖
            </button>
            <h2 className="text-xl font-semibold mb-4">Create Client</h2>
            <form
              id="addClientForm"
              ref={addClientFormRef}
              className="flex-1 overflow-y-auto p-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (addClientSavingRef.current) return;

                // handle submission
                const formData = new FormData(e.target);
                if (!validatePhone(newClientPhone)) {
                  showError("Please enter a valid phone number.");
                  return;
                }
                const payload = {
                  name: formData.get("name"),
                  employmentOffice: formData.get("employmentOffice"),
                  phone: newClientPhone,
                  email: formData.get("email"),
                  primaryLocation: newClientPrimaryLocation,
                  primaryLocationName: newClientLocationName,
                  billingAddress: newClientBillingAddress,
                };

                addClientSavingRef.current = true;
                setAddClientSaving(true);
                try {
                  await API.post("/api/clients/clients", payload);
                  setShowAddClientModal(false);
                  resetAddClientForm();
                  patchQuery({ currentPage: 1 }); // refresh list
                  showSuccess("Client saved");
                } catch (err) {
                  console.error(err);
                  const errData = err?.response?.data || {};
                  showError(
                    errData.error || errData.message || "Error saving client",
                  );
                } finally {
                  addClientSavingRef.current = false;
                  setAddClientSaving(false);
                }
              }}
            >
              <div>
                <label className="block text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Employment Office <span className="text-red-500">*</span>
                </label>
                <select
                  name="employmentOffice"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="">Select Employment Office</option>
                  {(employmentOfficeOptions.length > 0
                    ? employmentOfficeOptions
                    : divisions
                  ).map((o, index) => {
                    const officeName =
                      typeof o.name === "string" ? o.name : String(o.name || "");
                    return (
                      <option
                        key={o.office_id || o.id || o.division_id || index}
                        value={officeName}
                      >
                        {officeName}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={newClientPhone}
                  onChange={setNewClientPhone}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Primary Location <span className="text-red-500">*</span>
                </label>
                <AddressAutocomplete
                  value={newClientPrimaryLocation}
                  onChange={(v) => {
                    console.log('🔍 onChange received:', v);
                    setNewClientPrimaryLocation(v);
                  }}
                  onManualSave={(data) => {
                    console.log('🔍 onManualSave received:', data);
                    // data = { address: {street, city, region, zip, country}, name: "..." }
                    // The address with name is already set via onChange
                    // Just update the separate name state
                    if (data.name) {
                      setNewClientLocationName(data.name);
                    }
                  }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Billing Address <span className="text-red-500">*</span>
                </label>
                <AddressAutocomplete
                  value={newClientBillingAddress}
                  onChange={(v) => setNewClientBillingAddress(v)}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={addClientSaving}
                  onClick={() => {
                    if (addClientSaving) return;
                    setShowAddClientModal(false);
                    resetAddClientForm();
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addClientSaving}
                  className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                >
                  {addClientSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubClientModal && parentClient && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-full flex flex-col overflow-hidden relative">
            {/* Close button */}
            <button
              onClick={() => {
                if (subClientSaving) return;
                setShowSubClientModal(false);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
              disabled={subClientSaving}
            >
              ✖
            </button>

            {/* Header */}
            <h2 className="text-xl font-semibold p-6 border-b">
              Create Sub-client
            </h2>

            {/* Scrollable form */}
            <form
              id="subClientForm"
              className="flex-1 overflow-y-auto p-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (subClientSaving) return;

                const formData = new FormData(e.target);

                const payload = {
                  name: formData.get("name"),
                  employmentOffice: formData.get("employmentOffice"),
                  phone: subClientPhone,
                  primaryLocation: subClientPrimaryLocation,
                  billingAddress: subClientBillingAddress,
                  parentClientId: parentClient.id,
                };

                if (!validatePhone(payload.phone)) {
                  showError("Please enter a valid phone number.");
                  return;
                }

                setSubClientSaving(true);
                try {
                  const res = await fetch(
                    `${BASE_URL}/api/clients/clients/subclient`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                    },
                  );

                  if (res.ok) {
                    setShowSubClientModal(false);
                    setSubClientPhone("");
                    setSubClientPrimaryLocation("");
                    setSubClientBillingAddress("");
                    patchQuery({ currentPage: 1 }); // refresh list
                    setReloadKey((prev) => prev + 1);
                    showSuccess("Sub-client saved");
                  } else {
                    const errData = await res.json();
                    showError(
                      errData.error ||
                      errData.message ||
                      "Error saving sub-client",
                    );
                  }
                } catch (err) {
                  console.error(err);
                  showError("Server error");
                } finally {
                  setSubClientSaving(false);
                }
              }}
            >
              <div>
                <label className="block text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Employment Office <span className="text-red-500">*</span>
                </label>
                <select
                  name="employmentOffice"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="">Select Employment Office</option>
                  {(employmentOfficeOptions.length > 0
                    ? employmentOfficeOptions
                    : divisions
                  ).map((o, index) => {
                    const officeName =
                      typeof o.name === "string" ? o.name : String(o.name || "");
                    return (
                      <option
                        key={o.office_id || o.id || o.division_id || index}
                        value={officeName}
                      >
                        {officeName}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={subClientPhone}
                  onChange={setSubClientPhone}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  className="w-full"
                />
              </div>
              {/*
              <div>
                <label className="block text-sm font-medium">
                  CA <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="ca"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              */}
              <div>
                <label className="block text-sm font-medium">
                  Parent Client <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="parentClient"
                  readOnly
                  value={
                    parentClient?.name == null
                      ? ""
                      : typeof parentClient.name === "string"
                        ? parentClient.name
                        : typeof parentClient.name === "object" && parentClient.name !== null && "label" in parentClient.name
                          ? parentClient.name.label
                          : String(parentClient.name)
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100"
                />
              </div>
              {/*
              <div>
                <label className="block text-sm font-medium">
                  Testing <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="testing"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              */}
              <div>
                <label className="block text-sm font-medium">
                  Primary Location <span className="text-red-500">*</span>
                </label>
                <AddressAutocomplete
                  value={subClientPrimaryLocation}
                  onChange={(v) => setSubClientPrimaryLocation(v)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Billing Address <span className="text-red-500">*</span>
                </label>
                <AddressAutocomplete
                  value={subClientBillingAddress}
                  onChange={(v) => setSubClientBillingAddress(v)}
                  required
                />
              </div>
            </form>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                type="button"
                onClick={() => setShowSubClientModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={subClientSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="subClientForm"
                className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={subClientSaving}
              >
                {subClientSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Removed Client Sidebar Drawer - keeping only ClientDetailModal */}

      {/* Client Detail Modal */}
      <ClientDetailModal
        client={selectedClient}
        isOpen={showClientDetailModal}
        selectedTab={selectedTab}
        readOnly={clientsReadOnly}
        onClose={() => {
          setShowClientDetailModal(false);
          setSelectedClient(null);
        }}
        onSaved={() => setReloadKey((k) => k + 1)}
      />

      {/* Position Detail Modal */}
      <PositionDetailModal
        position={selectedPosition}
        isOpen={showPositionDetailModal}
        readOnly={clientsReadOnly}
        onClose={() => {
          setShowPositionDetailModal(false);
          setSelectedPosition(null);
        }}
        onSaved={() => setReloadKey((k) => k + 1)}
      />

      {/* Contact Detail Modal */}
      <ContactDetailModal
        contact={selectedContact}
        clientId={selectedContact?.clientId ?? selectedContact?.client_id}
        isOpen={showContactDetail}
        initialTab={contactDetailInitialTab}
        readOnly={clientsReadOnly}
        onClose={() => {
          setShowContactDetail(false);
          setSelectedContact(null);
          setContactDetailInitialTab("profile");
        }}
        onSaved={() => setReloadKey((k) => k + 1)}
      />

      {showAssignContactModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            <button
              onClick={closeAssignContactModal}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
            <h2 className="text-xl font-semibold mb-4">Assign Contact to Client</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Contact</label>
                <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-sm">
                  {assignContact?.name || ""}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Client <span className="text-red-500">*</span>
                </label>
                <select
                  value={assignClientId}
                  onChange={(e) => {
                    setAssignClientId(e.target.value);
                    setAssignLocationId("");
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="">Select client</option>
                  {assignClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium">Location (optional)</label>
                <select
                  value={assignLocationId}
                  onChange={(e) => setAssignLocationId(e.target.value)}
                  disabled={!assignClientId}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white disabled:bg-gray-50"
                >
                  <option value="">Select location</option>
                  {assignLocations.map((l) => (
                    <option key={l.location_id} value={l.location_id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Roles <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {assignRoles.length === 0 ? (
                    <div className="text-sm text-gray-500">No roles found</div>
                  ) : (
                    assignRoles.map((role) => {
                      const rid = Number(role.id ?? role.role_id);
                      const checked = assignRoleIds.includes(rid);
                      return (
                        <label key={rid} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssignRole(rid)}
                          />
                          <span>{role.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeAssignContactModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignContactToClient}
                disabled={assignSaving}
                className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {assignSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Clients;

function AddPositionForm({ BASE_URL, onSuccess, onCancel }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [positions, setPositions] = useState([]);
  const [positionInput, setPositionInput] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [clientsRes, positionsRes] = await Promise.all([
          fetch(`${BASE_URL}/api/clients/clients?all=1`),
          fetch(`${BASE_URL}/api/clients/positions?all=1`),
        ]);
        const clientsJson = await clientsRes.json();
        const positionsJson = await positionsRes.json();
        setClients(clientsJson.data || []);
        const rawPositions = Array.isArray(positionsJson.data) ? positionsJson.data : [];
        const seenNames = new Set();
        const uniquePositions = [];

        for (const position of rawPositions) {
          const positionName =
            typeof position?.name === "string"
              ? position.name.trim()
              : String(position?.name || "").trim();

          if (!positionName) continue;

          const key = positionName.toLowerCase();
          if (seenNames.has(key)) continue;

          seenNames.add(key);
          uniquePositions.push({
            ...position,
            name: positionName,
          });
        }

        setPositions(uniquePositions);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [BASE_URL]);

  useEffect(() => {
    if (!clientId) {
      setDivisions([]);
      setPositionInput("");
      setDivisionId("");
      return;
    }
    (async () => {
      try {
        console.log(`🔍 Fetching divisions for client ${clientId}`);
        const divRes = await fetch(
          `${BASE_URL}/api/clients/divisions/by-client/${clientId}`,
          { credentials: 'include' }
        );
        if (!divRes.ok) {
          console.error(`❌ Divisions fetch failed: ${divRes.status}`);
          setDivisions([]);
          return;
        }
        const div = await divRes.json();
        console.log(`✅ Divisions loaded:`, div.data);
        setDivisions(div.data || []);
      } catch (e) {
        console.error("❌ Error fetching divisions:", e);
        setDivisions([]);
      }
    })();
  }, [clientId, BASE_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) return showError("Client is required");
    if (!positionInput.trim())
      return showError("Position is required");
    try {
      const trimmed = positionInput.trim();
      // Always create a brand-new position row for this client.
      // Existing position names are only used as suggestions; we do NOT
      // reuse or overwrite them here.

      const res = await fetch(`${BASE_URL}/api/clients/positions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId,
          positionId: null,
          positionName: trimmed,
          divisionId: divisionId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");

      // Determine the new/mapped position id returned by the backend
      const newPositionId =
        (data && (data.positionId || data.id)) || null;

      // Resolve friendly names for client and division
      let clientName = "";
      if (clientId) {
        const foundClient = clients.find(
          (c) => String(c.id) === String(clientId)
        );
        clientName = foundClient?.name || "";
      }

      let divisionName = "";
      if (divisionId) {
        const foundDiv = divisions.find(
          (d) => String(d.id ?? d.division_id ?? d.value) === String(divisionId)
        );
        divisionName =
          (typeof foundDiv?.name === "string"
            ? foundDiv.name
            : foundDiv?.label) || "";
      }

      // Build a minimal position object that PositionDetailModal can edit
      const createdPosition = newPositionId
        ? {
          id: newPositionId,
          name: trimmed,
          clientId,
          clientName,
          divisionName,
          bill_rate: null,
          pay_rate: null,
          unpaid_break: 0,
          uniform: "",
          requirements: "",
        }
        : null;

      setClientId("");
      setPositionInput("");
      setDivisionId("");
      if (onSuccess) onSuccess(createdPosition);
    } catch (err) {
      console.error(err);
      showError(err.message || "Server error");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-700">
          Client <span className="text-red-500">*</span>
        </label>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-buttonRed focus:border-transparent"
        >
          <option value="">Select client</option>
          {(clients || []).map((c) => {
            const name = typeof c.name === 'string' ? c.name : String(c.name || '');
            return (
              <option key={c.id} value={c.id}>
                {name} {c.state === "deleted" ? "(deleted)" : ""}
              </option>
            );
          })}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-700">
          Position <span className="text-red-500">*</span>
        </label>
        <select
          value={positionInput}
          onChange={(e) => setPositionInput(e.target.value)}
          className={`border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-buttonRed focus:border-transparent ${!clientId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
          disabled={!clientId}
        >
          <option value="">Select position</option>
          {positions.map((p) => {
            // Ensure name is a string (not an object)
            const name = typeof p.name === 'string' ? p.name : String(p.name || '');
            return (
              <option key={p.id} value={name}>
                {name}
              </option>
            );
          })}
        </select>
        {!clientId && (
          <p className="text-xs text-gray-500">Select a client first</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-700">
          Division <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <select
          value={divisionId}
          onChange={(e) => setDivisionId(e.target.value)}
          className={`border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-buttonRed focus:border-transparent ${!clientId ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
          disabled={!clientId}
        >
          <option value="">Select division (optional)</option>
          {(divisions || []).map((d, idx) => {
            const id = d.id ?? d.division_id ?? d.value;
            const name =
              typeof d.name === "string"
                ? d.name
                : typeof d.label === "string"
                  ? d.label
                  : String(d.name ?? d.label ?? "");
            return (
              <option key={id ?? idx} value={id ?? ""}>
                {name}
              </option>
            );
          })}
        </select>
        {!clientId ? (
          <p className="text-xs text-gray-500">Select a client first</p>
        ) : (
          <p className="text-xs text-gray-500">Leave empty for client-wide position</p>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-2">
        <button
          type="button"
          className="w-[100px] border-2 border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors"
          onClick={onCancel}
        >
          CANCEL
        </button>
        <button
          type="submit"
          disabled={!clientId || !positionInput.trim()}
          className="w-[100px] bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-3xl text-sm font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          CREATE
        </button>
      </div>
    </form>
  );
}
